import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { userService } from '../../services/userService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { UserWithDetails, UserUpdate, UserGroup, UserRole } from '../../types/user';
import config from '../../config/env';

// Role hierarchy for filtering (higher index = higher privilege)
const ROLE_HIERARCHY = ['Viewer', 'ClientAdmin', 'Admin', 'SuperAdmin'];

// Backward compat: treat Operator as ClientAdmin
const normalizeRoleName = (role: string): string => role === 'Operator' ? 'ClientAdmin' : role;

interface UserDetailModalProps {
  user: UserWithDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
}

type TabType = 'details' | 'groups' | 'roles' | 'password';

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  user,
  isOpen,
  onClose,
  onUserUpdated
}) => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState<string>('');
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  // Role management state
  const [clientRoles, setClientRoles] = useState<UserRole[]>([]);
  const [selectedClient, setSelectedClient] = useState(config.oauth.clientId);
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState<string>('');
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  // Check if current user is SuperAdmin
  const isSuperAdmin = useMemo(() => {
    return currentUser?.roles?.includes('SuperAdmin') || false;
  }, [currentUser?.roles]);

  // Get the highest role level of the current user
  const currentUserRoleLevel = useMemo(() => {
    if (!currentUser?.roles) return -1;
    let maxLevel = -1;
    for (const role of currentUser.roles) {
      const level = ROLE_HIERARCHY.indexOf(normalizeRoleName(role));
      if (level > maxLevel) maxLevel = level;
    }
    return maxLevel;
  }, [currentUser?.roles]);

  // Get available roles that the current user can assign (only roles at or below their level)
  const availableRoles = useMemo(() => {
    if (isSuperAdmin) {
      return ROLE_HIERARCHY; // SuperAdmin can assign all roles
    }
    return ROLE_HIERARCHY.filter((_, index) => index <= currentUserRoleLevel);
  }, [isSuperAdmin, currentUserRoleLevel]);

  // Edit form state
  const [editEmail, setEditEmail] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEnabled, setEditEnabled] = useState(true);
  const [editEmailVerified, setEditEmailVerified] = useState(false);

  // Password reset state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState(true);

  // Initialize form when user changes
  useEffect(() => {
    if (user) {
      setEditEmail(user.email || '');
      setEditFirstName(user.firstName || '');
      setEditLastName(user.lastName || '');
      setEditEnabled(user.enabled);
      setEditEmailVerified(user.emailVerified);
      setError(null);
      setSuccess(null);

      // Fetch user groups
      fetchUserGroups(user.id);

      // Fetch all clients for SuperAdmin
      if (isSuperAdmin) {
        clientService.fetchActiveClients().then(setAllClients).catch(console.error);
      }
    }
  }, [user, isSuperAdmin]);

  const fetchUserGroups = async (userId: string) => {
    try {
      const userGroups = await userService.getUserGroups(userId);
      setGroups(userGroups);

      // Extract client UUIDs from group paths and resolve names
      const clientUuids = new Set<string>();
      userGroups.forEach(group => {
        const match = group.path.match(/\/Clients\/([a-f0-9-]+)/i);
        if (match) {
          clientUuids.add(match[1]);
        }
      });

      // Resolve client names
      if (clientUuids.size > 0) {
        const nameMap = new Map<string, string>();
        for (const uuid of clientUuids) {
          const name = await clientService.getClientName(uuid);
          nameMap.set(uuid, name);
        }
        setClientNames(nameMap);
      }
    } catch (err) {
      console.error('[USER DETAIL] Failed to fetch groups:', err);
      setGroups([]);
    }
  };

  const handleAddClientGroup = async () => {
    if (!user || !selectedClientToAdd) return;

    setIsLoadingGroups(true);
    setError(null);

    try {
      // Get the group info for the selected client
      const groupInfo = await clientService.getClientGroup(selectedClientToAdd);
      if (!groupInfo) {
        setError('Failed to get client group information');
        return;
      }

      await userService.addUserToGroup(user.id, groupInfo.id);
      setSuccess('User added to client group successfully');
      setSelectedClientToAdd('');

      // Refresh groups
      await fetchUserGroups(user.id);
    } catch (err: any) {
      console.error('[USER DETAIL] Error adding user to client group:', err);
      let errorMessage = 'Failed to add user to client group';
      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const handleRemoveClientGroup = async (groupId: string, groupName: string) => {
    if (!user) return;

    if (!window.confirm(`Are you sure you want to remove this user from the "${groupName}" client group?`)) {
      return;
    }

    setIsLoadingGroups(true);
    setError(null);

    try {
      await userService.removeUserFromGroup(user.id, groupId);
      setSuccess('User removed from client group successfully');

      // Refresh groups
      await fetchUserGroups(user.id);
    } catch (err: any) {
      console.error('[USER DETAIL] Error removing user from client group:', err);
      let errorMessage = 'Failed to remove user from client group';
      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  // Get clients that the user is NOT already a member of
  const availableClientsToAdd = useMemo(() => {
    const userClientIds = new Set<string>();
    groups.forEach(group => {
      const match = group.path.match(/\/Clients\/([a-f0-9-]+)/i);
      if (match) {
        userClientIds.add(match[1]);
      }
    });
    return allClients.filter(client => !userClientIds.has(client.id));
  }, [groups, allClients]);

  const fetchClientRoles = async (userId: string, clientId: string) => {
    setIsLoadingRoles(true);
    try {
      const response = await userService.getUserClientRoles(userId, clientId);
      setClientRoles(response.roles || []);
    } catch (err) {
      console.error('[USER DETAIL] Failed to fetch client roles:', err);
      setClientRoles([]);
      setError('Failed to load client roles');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  // Effect to fetch roles when Roles tab is opened
  useEffect(() => {
    if (activeTab === 'roles' && user) {
      fetchClientRoles(user.id, selectedClient);
    }
  }, [activeTab, selectedClient, user]);

  const handleSave = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const updateData: UserUpdate = {
        email: editEmail || undefined,
        firstName: editFirstName || undefined,
        lastName: editLastName || undefined,
        enabled: editEnabled,
        emailVerified: editEmailVerified
      };

      await userService.updateUser(user.id, updateData);

      setSuccess('User updated successfully');
      setIsEditing(false);

      // Notify parent to refresh
      setTimeout(() => {
        onUserUpdated();
      }, 1000);
    } catch (err: any) {
      console.error('[USER DETAIL] Error updating user:', err);

      let errorMessage = 'Failed to update user';
      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user) return;

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await userService.resetPassword(user.id, {
        password: newPassword,
        temporary: temporaryPassword
      });

      setSuccess('Password reset successfully');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      console.error('[USER DETAIL] Error resetting password:', err);

      let errorMessage = 'Failed to reset password';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    if (!window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await userService.deleteUser(user.id);
      setSuccess('User deleted successfully');

      // Close and refresh
      setTimeout(() => {
        onUserUpdated();
      }, 1000);
    } catch (err: any) {
      console.error('[USER DETAIL] Error deleting user:', err);

      let errorMessage = 'Failed to delete user';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!user || !selectedRoleToAdd) return;

    setIsLoadingRoles(true);
    setError(null);

    try {
      await userService.addClientRoleToUser(user.id, selectedClient, selectedRoleToAdd);
      setSuccess(`Role "${selectedRoleToAdd}" added successfully`);
      setSelectedRoleToAdd('');

      // Refresh roles
      await fetchClientRoles(user.id, selectedClient);
    } catch (err: any) {
      console.error('[USER DETAIL] Error adding role:', err);

      let errorMessage = 'Failed to add role';
      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  // Check if current user can manage a specific role
  const canManageRole = (roleName: string): boolean => {
    if (isSuperAdmin) return true;
    const roleLevel = ROLE_HIERARCHY.indexOf(normalizeRoleName(roleName));
    return roleLevel >= 0 && roleLevel <= currentUserRoleLevel;
  };

  const handleRemoveRole = async (roleName: string) => {
    if (!user) return;

    // Check if current user can manage this role
    if (!canManageRole(roleName)) {
      setError(`You don't have permission to remove the "${roleName}" role`);
      return;
    }

    if (!window.confirm(`Are you sure you want to remove the "${roleName}" role?`)) {
      return;
    }

    setIsLoadingRoles(true);
    setError(null);

    try {
      await userService.removeClientRoleFromUser(user.id, selectedClient, roleName);
      setSuccess(`Role "${roleName}" removed successfully`);

      // Refresh roles
      await fetchClientRoles(user.id, selectedClient);
    } catch (err: any) {
      console.error('[USER DETAIL] Error removing role:', err);

      let errorMessage = 'Failed to remove role';
      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    setActiveTab('details');
    setError(null);
    setSuccess(null);
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  const { formatDateTime, t } = useI18n();
  const formatDate = (date?: Date): string => {
    if (!date) return 'N/A';
    return formatDateTime(date);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="client-detail-modal user-detail-modal" data-testid="modal-user-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>User: {user.username}</h2>
            <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
              {user.enabled ? t('users.enabledBadge') : t('users.disabledBadge')}
            </span>
          </div>
          <button
            className="close-button"
            onClick={handleClose}
            disabled={isLoading}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
            data-testid="tab-user-details"
            onClick={() => setActiveTab('details')}
          >
            {t('tabs.details')}
          </button>
          <button
            className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
            data-testid="tab-user-groups"
            onClick={() => setActiveTab('groups')}
          >
            {t('tabs.groups')} ({groups.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
            data-testid="tab-user-roles"
            onClick={() => setActiveTab('roles')}
          >
            {t('tabs.roles')} ({clientRoles.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
            data-testid="tab-user-password"
            onClick={() => setActiveTab('password')}
          >
            {t('tabs.password')}
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-banner">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} aria-label={t('common.dismissError')}>x</button>
            </div>
          )}

          {success && (
            <div className="success-banner">
              <span>{success}</span>
              <button type="button" onClick={() => setSuccess(null)} aria-label={t('common.close')}>x</button>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="tab-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('users.userId')}</label>
                  <span className="info-value monospace">{user.id}</span>
                </div>
                <div className="info-item">
                  <label>{t('users.username')}</label>
                  <span className="info-value monospace">{user.username}</span>
                </div>
                <div className="info-item">
                  <label>{t('users.email')}</label>
                  {isEditing ? (
                    <input
                      type="email"
                      data-testid="field-user-email-edit"
                      className="edit-input"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      disabled={isLoading}
                      autoFocus
                    />
                  ) : (
                    <span className="info-value">{user.email || '—'}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>{t('users.firstName')}</label>
                  {isEditing ? (
                    <input
                      type="text"
                      data-testid="field-user-first-name-edit"
                      className="edit-input"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      disabled={isLoading}
                    />
                  ) : (
                    <span className="info-value">{user.firstName || '—'}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>{t('users.lastName')}</label>
                  {isEditing ? (
                    <input
                      type="text"
                      data-testid="field-user-last-name-edit"
                      className="edit-input"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      disabled={isLoading}
                    />
                  ) : (
                    <span className="info-value">{user.lastName || '—'}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>{t('users.fullName')}</label>
                  <span className="info-value">{user.fullName || '—'}</span>
                </div>
                <div className="info-item">
                  <label>{t('users.accountStatus')}</label>
                  {isEditing ? (
                    <select
                      data-testid="field-user-enabled-edit"
                      className="edit-select status-select"
                      value={editEnabled ? 'active' : 'inactive'}
                      onChange={(e) => setEditEnabled(e.target.value === 'active')}
                      disabled={isLoading}
                    >
                      <option value="active">{t('users.enabled')}</option>
                      <option value="inactive">{t('users.disabled')}</option>
                    </select>
                  ) : (
                    <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                      {user.enabled ? t('users.enabled') : t('users.disabled')}
                    </span>
                  )}
                </div>
                <div className="info-item">
                  <label>{t('users.emailVerified')}</label>
                  {isEditing ? (
                    <select
                      data-testid="field-user-email-verified-edit"
                      className="edit-select status-select"
                      value={editEmailVerified ? 'active' : 'inactive'}
                      onChange={(e) => setEditEmailVerified(e.target.value === 'active')}
                      disabled={isLoading}
                    >
                      <option value="active">{t('common.yes')}</option>
                      <option value="inactive">{t('common.no')}</option>
                    </select>
                  ) : (
                    <span className={`status-badge ${user.emailVerified ? 'active' : 'inactive'}`}>
                      {user.emailVerified ? t('common.yes') : t('common.no')}
                    </span>
                  )}
                </div>
                <div className="info-item">
                  <label>{t('users.created')}</label>
                  <span className="info-value">{formatDate(user.createdDate)}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'groups' && (
            <div className="tab-content">
              <div className="groups-list">
                <h3>{t('users.currentClientGroups')}</h3>
                {groups.length > 0 ? (
                  <ul>
                    {groups.map((group) => {
                      // Resolve group name if it's a client UUID
                      const displayName = clientNames.get(group.name) || group.name;
                      const isClientGroup = group.path.startsWith('/Clients/');
                      return (
                        <li key={group.id} className="group-item">
                          <div className="group-info">
                            <span className="group-name">{displayName}</span>
                            <span className="group-path">{group.path}</span>
                          </div>
                          {isSuperAdmin && isClientGroup && (
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => handleRemoveClientGroup(group.id, displayName)}
                              disabled={isLoadingGroups}
                            >
                              {t('users.remove')}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="no-groups">{t('users.notAnyGroup')}</p>
                )}
              </div>

              {/* Add Client Group section - SuperAdmin only */}
              {isSuperAdmin && (
                <div className="add-group-section">
                  <h3>{t('users.addClientGroup')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('clients.client')}</label>
                      <select
                        value={selectedClientToAdd}
                        data-testid="field-user-add-group-client"
                        onChange={(e) => setSelectedClientToAdd(e.target.value)}
                        disabled={isLoadingGroups}
                      >
                        <option value="">{t('users.selectClient')}</option>
                        {availableClientsToAdd.map(client => (
                          <option key={client.id} value={client.id}>{client.name}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="btn btn-primary"
                      data-testid="btn-user-add-group"
                      onClick={handleAddClientGroup}
                      disabled={isLoadingGroups || !selectedClientToAdd}
                    >
                      {isLoadingGroups ? t('users.adding') : t('users.addToGroup')}
                    </button>
                  </div>
                  {availableClientsToAdd.length === 0 && allClients.length > 0 && (
                    <p className="no-clients-hint">{t('users.alreadyInAllGroups')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="tab-content">
              <div className="roles-management">
                <div className="form-group">
                  <label>{t('users.resourceAccess')}</label>
                  <select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    disabled={isLoadingRoles}
                  >
                    <option value="provisioning-dev">provisioning-dev</option>
                    <option value="provisioning-prod">provisioning-prod</option>
                  </select>
                </div>

                <div className="roles-section">
                  <h3>{t('users.currentRoles')}</h3>
                  {isLoadingRoles ? (
                    <p>{t('users.loadingRoles')}</p>
                  ) : clientRoles.length > 0 ? (
                    <ul className="roles-list">
                      {clientRoles.map((role) => {
                        const canRemove = canManageRole(role.name);
                        return (
                          <li key={role.id} className="role-item">
                            <div className="role-info">
                              <span className="role-name">{role.name}</span>
                              {role.description && (
                                <span className="role-description">{role.description}</span>
                              )}
                            </div>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={() => handleRemoveRole(role.name)}
                              disabled={isLoadingRoles || !canRemove}
                              title={!canRemove ? t('users.cantRemoveHigherRole') : ''}
                            >
                              {t('users.remove')}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="no-roles">{t('users.noRolesForClient')}</p>
                  )}
                </div>

                <div className="add-role-section">
                  <h3>{t('users.addRole')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('users.role')}</label>
                      <select
                        value={selectedRoleToAdd}
                        data-testid="field-user-role-name"
                        onChange={(e) => setSelectedRoleToAdd(e.target.value)}
                        disabled={isLoadingRoles}
                      >
                        <option value="">{t('users.selectRole')}</option>
                        {availableRoles.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="btn btn-primary"
                      data-testid="btn-user-add-role"
                      onClick={handleAddRole}
                      disabled={isLoadingRoles || !selectedRoleToAdd}
                    >
                      {isLoadingRoles ? t('users.adding') : t('users.addRole')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="tab-content">
              <div className="password-reset-form" data-testid="form-user-password">
                <div className="form-group">
                  <label>{t('users.newPassword')}</label>
                  <input
                    type="password"
                    data-testid="field-user-password-new"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('users.newPasswordPlaceholder')}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label>{t('users.confirmPassword')}</label>
                  <input
                    type="password"
                    data-testid="field-user-password-confirm"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('users.confirmNewPasswordPlaceholder')}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      data-testid="field-user-password-temporary"
                      checked={temporaryPassword}
                      onChange={(e) => setTemporaryPassword(e.target.checked)}
                      disabled={isLoading}
                    />
                    {t('users.temporaryPassword')}
                  </label>
                </div>
                <button
                  className="btn btn-primary"
                  data-testid="btn-user-reset-password"
                  onClick={handlePasswordReset}
                  disabled={isLoading || !newPassword || !confirmPassword}
                >
                  {isLoading ? t('users.resetting') : t('users.resetPassword')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          {activeTab === 'details' ? (
            !isEditing ? (
              <>
                <div className="action-group">
                  <button
                    className="btn btn-danger"
                    data-testid="btn-user-delete"
                    onClick={handleDelete}
                    disabled={isLoading}
                  >
                    {t('users.deleteUser')}
                  </button>
                </div>
                <div className="action-group">
                  <button
                    className="btn btn-primary"
                    data-testid="btn-user-edit"
                    onClick={() => setIsEditing(true)}
                    disabled={isLoading}
                  >
                    {t('common.edit')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="action-group">
                  <button
                    className="btn btn-secondary"
                    data-testid="btn-user-cancel"
                    onClick={() => {
                      setIsEditing(false);
                      // Reset to original values
                      setEditEmail(user.email || '');
                      setEditFirstName(user.firstName || '');
                      setEditLastName(user.lastName || '');
                      setEditEnabled(user.enabled);
                      setEditEmailVerified(user.emailVerified);
                    }}
                    disabled={isLoading}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
                <div className="action-group">
                  <button
                    className="btn btn-primary"
                    data-testid="btn-user-save"
                    onClick={handleSave}
                    disabled={isLoading}
                  >
                    {isLoading ? t('users.saving') : t('users.saveChanges')}
                  </button>
                </div>
              </>
            )
          ) : (
            <>
              <div className="action-group" />
              <button
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={isLoading}
              >
                {t('common.close')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
